import { Toolbar, Box, useTheme, useMediaQuery, type Theme, Link } from '@mui/material';
import { ReactNode } from 'react';
import { useHandleSearch } from '../SearchBar/useHandleSearch';
import { SearchBar, SearchBarProps } from '../SearchBar/SearchBar';
import { GlobalNavProps } from '../GlobalNav/GlobalNav.types';
import { LOCAL_NAV_HEIGHT } from '../styles';
import { HXCIcon, Icons } from '../../HXCIcon';

export interface LocalNavProps extends Pick<
  SearchBarProps,
  'onSearch' | 'searchPlaceholder' | 'searchOptions'
> {
  showMyOrderLink: boolean;
  myOrderHref: string;
  linkComponent: GlobalNavProps['linkComponent'];
  /*
   * OPTIONAL: Buttons to appear on the right hand side of the header
   */
  buttons: ReactNode;
  currentUrl: string;
  initialSearchTerm?: string;
  /*
   * OPTIONAL: Auth state to trigger drawer close on login/logout
   */
  authState?: string | null;
  /**
   * ePharmacy-only: “My Orders” in the mobile drawer. Defaults to true.
   * Marketing sites (Devices) should pass false.
   */
  showOrderHistoryLink?: boolean;
  /**
   * ePharmacy-only: UK / Ireland switcher in the mobile drawer. Defaults to true.
   * Marketing sites (Devices) should pass false.
   */
  showIrelandPrompt?: boolean;
}

export const LocalNav = ({
  onSearch,
  buttons,
  searchPlaceholder = 'Search',
  searchOptions = [],
  linkComponent,
  initialSearchTerm,
}: LocalNavProps) => {
  const theme = useTheme();
  const isSmallScreen = useMediaQuery((mediaTheme: Theme) => mediaTheme.breakpoints.down(720));

  const { handleSearch, searchTerm, anchorEl, setAnchorEl } = useHandleSearch(
    onSearch,
    initialSearchTerm,
  );

  return (
    <Toolbar
      data-component="LocalNav"
      sx={{
        height: `${LOCAL_NAV_HEIGHT}px`,
        width: '100%',
        backgroundColor: theme.palette.black.main,
        display: isSmallScreen ? 'none' : 'flex',
        justifyContent: 'space-between',
        boxSizing: 'border-box',
      }}
    >
      <Box sx={{ display: 'flex', flex: '1', alignItems: 'center' }}>
        <Link
          component={linkComponent}
          to={'/'}
          aria-label={'Go to homepage'}
          role={'link'}
          sx={{ display: 'flex', alignItems: 'center' }}
        >
          <HXCIcon
            name={Icons.HxBasicWhiteLogo}
            color={'white'}
            sx={{ width: '187px', height: '45px' }} // specific dimensions for the logo
          />
        </Link>
      </Box>

      <Box sx={{ flex: '1', maxWidth: '400px' }}>
        {Boolean(onSearch) && (
          <SearchBar
            onSearch={onSearch}
            handleSearch={handleSearch}
            searchTerm={searchTerm}
            searchPlaceholder={searchPlaceholder}
            searchOptions={searchOptions}
            anchorEl={anchorEl}
            setAnchorEl={setAnchorEl}
          />
        )}
      </Box>

      <Box sx={{ height: '46px', justifyContent: 'flex-end', display: 'flex', flex: '1' }}>
        {buttons}
      </Box>
    </Toolbar>
  );
};
